import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from 'rhinostyle';
import { ATTACHMENT_CATEGORY } from '../helpers/AttachmentHelpers';
import { isAndroid } from '../helpers/BrowserHelpers';
import { fetchUser } from '../reducers/userReducer';
import { handleDownloadClick } from '../helpers/PdfViewerHelpers';

const DOWNLOAD_WARNING = 'While this file is encrypted during transmission, once downloaded, you are responsible for implementing appropriate safeguards to protect the data, including encryption or deletion to the extent required by applicable law.'; // eslint-disable-line max-len
const UNSUPPORTED_MESSAGE = 'This file can’t be previewed in this browser. Click the download button above to view it.';

// A sharelink's short URL has no extension, but the presigned download URL we resolve on
// open does (its path ends in the real filename). Use that extension to pick the player
// directly — far more reliable than probing with a hidden media element.
const AUDIO_EXTS = ['mp3', 'm4a', 'aac', 'wav', 'ogg', 'oga', 'opus', 'weba', 'amr'];
const VIDEO_EXTS = ['mp4', 'mov', 'm4v', 'webm', 'ogv', 'avi', 'mkv', '3gp', '3gpp', '3g2'];
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif', 'tif', 'tiff'];

function linkModeFromUrl(url) {
  if (!url) return null;
  let ext = '';
  try { ext = new URL(url).pathname.split('.').pop().toLowerCase(); } catch { return null; }
  if (AUDIO_EXTS.includes(ext)) return 'audio';
  if (VIDEO_EXTS.includes(ext)) return 'video';
  if (IMAGE_EXTS.includes(ext)) return 'image';
  return null;
}

// Unified attachment viewer, styled to match the image lightbox: a translucent
// overlay with a top toolbar (download / close) and centered content. Media the
// browser supports plays inline; short-links and anything it can't render inline
// show a prompt to download/open.
const AttachmentViewer = (props) => {
  const { open, onClose, attachment } = props;
  const dispatch = useDispatch();
  const isNativeApp = useSelector((state) => state.nativeApp.isNativeApp);
  const currentUserId = useSelector((state) => state.auth.currentUser);
  const isNativeAndroid = isNativeApp && isAndroid();

  const { downloadURL, filename, category, shareLinkUrl } = attachment || {};
  const downloadHref = downloadURL || shareLinkUrl;

  // Media streams directly from the authenticated route (the <video>/<audio> element
  // requests it and follows the 302 to S3) — no buffering the whole file first, so it
  // starts fast like the lightbox. onError catches a container the browser can't decode.
  const [previewFailed, setPreviewFailed] = useState(false);
  // Remount key + one-shot guard for the media self-heal (see handleMediaError).
  const [mediaRetry, setMediaRetry] = useState(0);
  const mediaRetriedRef = useRef(false);
  // Short-links have an unknown type, so we probe with native elements. Start in
  // 'probing' (a silent, hidden <video> that only reads metadata), then resolve to
  // 'video' / 'audio' / 'image' / 'failed'.
  const [linkMode, setLinkMode] = useState('probing');
  // Sharelink downloads: a short link has no file extension, so it can't force a
  // download on web (no Content-Disposition) or on native (the shell needs an extension
  // to treat a navigation as a download). Resolve the short code server-side to a
  // presigned, attachment-disposition S3 URL (path ends in the real filename) and use
  // THAT as the download href. See reference_sharelinks_vs_attachments.
  const [linkDownloadUrl, setLinkDownloadUrl] = useState(null);

  useEffect(() => {
    setPreviewFailed(false);
    setMediaRetry(0);
    mediaRetriedRef.current = false;
    setLinkMode('probing');
  }, [open, downloadHref, category]);

  useEffect(() => {
    setLinkDownloadUrl(null);
    if (!open || category !== ATTACHMENT_CATEGORY.LINK || !shareLinkUrl) return undefined;
    let cancelled = false;
    try {
      const code = new URL(shareLinkUrl).pathname.replace(/^\/+/, '');
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/shortlinks/${code}/download-url`)
        .then(({ data }) => { if (!cancelled && data?.url) setLinkDownloadUrl(data.url); })
        .catch(() => { /* fall back to the plain short link href */ });
    } catch { /* malformed short link; fall back below */ }
    return () => { cancelled = true; };
  }, [open, category, shareLinkUrl]);

  // Once the presigned URL resolves, its filename extension tells us the media type
  // directly — pick the player without the hidden-element probe (which is unreliable on
  // web). If the extension is unknown/ambiguous, leave it 'probing' to fall back to it.
  useEffect(() => {
    if (category !== ATTACHMENT_CATEGORY.LINK) return;
    const mode = linkModeFromUrl(linkDownloadUrl);
    if (mode) setLinkMode(mode);
  }, [category, linkDownloadUrl]);

  // Download href: a sharelink uses its presigned attachment URL (Content-Disposition forces
  // the download on web, and the native download handler saves it on device); a regular
  // attachment uses its route URL. Web regular-attachment downloads blob-save (see
  // handleDownload) since the route redirects to an inline S3 URL.
  const anchorHref = (category === ATTACHMENT_CATEGORY.LINK && linkDownloadUrl) ? linkDownloadUrl : downloadHref;

  if (!open) return null;

  // Media streams via the session cookie, which can lapse after ~30 min idle (rolling
  // cookie — any authenticated request refreshes it). On the first play error, refresh
  // the session and retry once before giving up (the "back from lunch" case).
  const handleMediaError = async () => {
    if (!mediaRetriedRef.current && currentUserId) {
      mediaRetriedRef.current = true;
      try { await dispatch(fetchUser(currentUserId, true)); } catch { /* ignore; retry anyway */ }
      setMediaRetry((n) => n + 1); // remount the element to reload with the refreshed cookie
      return;
    }
    setPreviewFailed(true);
  };

  const handleDownload = async (e) => {
    // Sharelink: anchorHref is a presigned attachment-disposition URL whose path ends in
    // the real filename, so a REAL click downloads it everywhere — web (disposition) and
    // native (external URL + extension). Just confirm and let the anchor navigate. (If the
    // presigned URL hasn't resolved yet, anchorHref is the plain short link, which on native
    // opens in Safari — the best we can do without the extension.)
    if (category === ATTACHMENT_CATEGORY.LINK) {
      if (!window.confirm(DOWNLOAD_WARNING)) e.preventDefault(); // eslint-disable-line no-alert
      return;
    }
    // Native: let the REAL click navigate to the external file URL. The rhinonative shell
    // only downloads when it sees a navigationType 'click' to an external URL that has a
    // file extension (App.handleNavigationStateChange → handleFileDownload). A blob: URL
    // or a JS/programmatic download never navigates, so the shell never fires. Only cancel
    // on decline of the warning.
    if (isNativeApp) {
      if (!window.confirm(DOWNLOAD_WARNING)) e.preventDefault(); // eslint-disable-line no-alert
      return;
    }
    // Web: the cross-origin route can't be force-downloaded by navigation, so fetch a
    // same-origin blob and save it (same as the image lightbox does on web).
    e.preventDefault();
    if (!window.confirm(DOWNLOAD_WARNING)) return; // eslint-disable-line no-alert
    try {
      const { data } = await axios.get(downloadHref, { responseType: 'blob' });
      handleDownloadClick(URL.createObjectURL(data), filename, isNativeApp, true);
    } catch {
      window.open(downloadHref, '_blank', 'noopener');
    }
  };

  const renderUnsupported = (showName) => (
    <div className="attachment-viewer__file">
      <Icon icon="attachment" className="attachment-viewer__file__icon" />
      {showName && filename && <div className="attachment-viewer__file__name">{filename}</div>}
      <div className="attachment-viewer__file__hint">{UNSUPPORTED_MESSAGE}</div>
    </div>
  );

  const renderBody = () => {
    if (category === ATTACHMENT_CATEGORY.VIDEO) {
      // Try to play; the browser's onError (undecodable container/codec) falls back to the
      // download prompt. We deliberately don't gate on canPlayType — Chrome reports '' for
      // types it can actually play (e.g. video/3gpp), which would wrongly block preview.
      if (previewFailed) return renderUnsupported(true);
      return (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video key={mediaRetry} className="attachment-viewer__media" src={downloadURL} controls autoPlay onError={handleMediaError} />
      );
    }

    if (category === ATTACHMENT_CATEGORY.AUDIO) {
      if (previewFailed) return renderUnsupported(true);
      return (
        <div className="attachment-viewer__audio-wrap">
          <Icon icon="speaker" className="attachment-viewer__file__icon" />
          <div className="attachment-viewer__file__name">{filename}</div>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio key={mediaRetry} className="attachment-viewer__audio" src={downloadURL} controls autoPlay onError={handleMediaError} />
        </div>
      );
    }

    // Short-links point at an unknown cross-origin file. Render the asset with native
    // elements (no iframe → no Chrome black media page). Probe by type: <video> plays
    // video and audio; if there's no video track it's audio-only, so switch to <audio>
    // (a plain control bar, no black). If it isn't playable media, try <img>, then a
    // download prompt.
    if (category === ATTACHMENT_CATEGORY.LINK) {
      if (linkMode === 'video') {
        return (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video className="attachment-viewer__media" src={shareLinkUrl} controls autoPlay />
        );
      }
      if (linkMode === 'audio') {
        return (
          <div className="attachment-viewer__audio-wrap">
            <Icon icon="speaker" className="attachment-viewer__file__icon" />
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio className="attachment-viewer__audio" src={shareLinkUrl} controls autoPlay />
          </div>
        );
      }
      if (linkMode === 'image') {
        return <img className="attachment-viewer__media" src={shareLinkUrl} alt={filename} onError={() => setLinkMode('failed')} />;
      }
      if (linkMode === 'failed') {
        return (
          <div className="attachment-viewer__file">
            <Icon icon="attachment" className="attachment-viewer__file__icon" />
            <div className="attachment-viewer__file__hint">Click the download button above to open this file.</div>
          </div>
        );
      }
      // 'probing': a silent, hidden <video> that only reads metadata to decide whether the
      // file has a video track (→ 'video') or is audio-only (→ 'audio'), or can't decode as
      // media (onError → 'image'). It is muted with no autoPlay so it never plays sound —
      // otherwise it would double up with the real <video>/<audio> we switch to next.
      return (
        <>
          <div className="attachment-viewer__loading">Loading…</div>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            style={{ display: 'none' }}
            src={shareLinkUrl}
            muted
            preload="metadata"
            onLoadedMetadata={(e) => setLinkMode(e.target.videoWidth ? 'video' : 'audio')}
            onError={() => setLinkMode('image')}
          />
        </>
      );
    }

    return renderUnsupported(true);
  };

  return createPortal(
    <div className="attachment-viewer" role="dialog" aria-modal="true" aria-label={filename}>
      <button type="button" className="attachment-viewer__backdrop" aria-label="Close" onClick={onClose} />
      <div className="attachment-viewer__toolbar">
        <a
          className="attachment-viewer__toolbar-btn attachment-viewer__download"
          href={anchorHref}
          download={filename}
          {...isNativeApp && !isNativeAndroid && { target: '_blank', rel: 'noopener noreferrer' }}
          onClick={handleDownload}
          aria-label="Download"
        >
          <Icon icon="download" />
        </a>
        <button type="button" className="attachment-viewer__toolbar-btn attachment-viewer__close" onClick={onClose} aria-label="Close">
          <Icon icon="close" />
        </button>
      </div>
      <div className="attachment-viewer__content">
        {renderBody()}
      </div>
    </div>,
    document.body,
  );
};

AttachmentViewer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  attachment: PropTypes.shape({
    downloadURL: PropTypes.string,
    filename: PropTypes.string,
    category: PropTypes.string,
    fileType: PropTypes.string,
    shareLinkUrl: PropTypes.string,
  }),
};

export default AttachmentViewer;
